import {
  Col,
  Row,
  Card,
  Input,
  Label,
  CardBody,
  CardTitle,
  FormFeedback,
} from 'reactstrap';
import './index.scss';
import {
  Tool,
  Shield,
  Droplet,
  Settings,
  CreditCard,
  HelpCircle,
  AlertOctagon,
  MessageSquare,
  Wind,
  Zap,
  Volume2,
  Columns,
} from 'react-feather';
import Select from 'react-select';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useClearSupportImage,
  useFetchSupportCategories,
  useFetchSupportSubCategories,
  useSupportCategories,
  useSupportSubCategories,
  useSupportSubSubCategories,
  useUploadServiceCallImage,
} from '../../../../store/support-management/SupportHooks';
import { selectThemeColors } from '../../../../utility/Utils';
import { useProfile } from '../../../../store/Auth/authHooks';
import { Controller, UseFormReturn } from 'react-hook-form';
import { ServiceCallFormValues } from '../AddServiceCallContent';
import {
  usePropertyManagementBuildings,
  useFetchPropertyManagementLocationTree,
  usePropertyManagementFloorsByBuildingVisitId,
} from '../../../../store/PropertiesManagement/propertyManagementHooks';
import { ServiceCallCategoryIconTypeEnum } from './service-call-category-icon-type.enum';
import UploadMultiImageInput from '../../../shared/UploadMultiImageInput/UploadMultiImageInput';

type PropsTypes = {
  form: UseFormReturn<any, ServiceCallFormValues>;
};

export const AddServiceCallForm = (props: PropsTypes) => {
  const profile = useProfile();
  const { t } = useTranslation();
  const supportCategories = useSupportCategories();
  const clearSupportImage = useClearSupportImage();
  const buildings = usePropertyManagementBuildings();
  const supportSubCategories = useSupportSubCategories();
  const supportSubSubCategories = useSupportSubSubCategories(
    props.form.watch('subCategoryId')
  );
  const uploadServiceCallFile = useUploadServiceCallImage();
  const fetchSupportCategories = useFetchSupportCategories();
  const floors = usePropertyManagementFloorsByBuildingVisitId(
    props.form.watch('buildingVisitId')
  );
  const fetchSupportSubCategories = useFetchSupportSubCategories();
  const fetchLocationTree = useFetchPropertyManagementLocationTree();
  const floorLabel = `Floor:  ${profile.company?.buildings[0].floor?.number},Space:  ${profile.company?.buildings[0].space_name}`;
  const [placesOptions, setPlacesOptions] = useState<
    {
      value: string;
      label: string;
    }[]
  >([]);

  const [buildingsOption, setBuildingsOption] = useState<
    {
      value: string;
      label: string;
    }[]
  >([]);
  const [chosenCategory, setChosenCategory] = useState<number>();
  const [isCategoryChosen, setIsCategoryChosen] = useState<boolean>(false);

  useEffect(() => {
    fetchLocationTree().finally();
    fetchSupportCategories().finally();
  }, []);

  useEffect(() => {
    if (props.form.watch('categoryId')) {
      fetchSupportSubCategories(props.form.watch('categoryId')).finally();
    }
  }, [props.form.watch('categoryId')]);

  useEffect(() => {
    if (!buildings) {
      return;
    }
    const options = buildings?.map((building) => {
      return {
        value: building.code,
        label: building.name,
      };
    });
    setBuildingsOption(options);
  }, [buildings]);

  useEffect(() => {
    if (!floors) {
      return;
    }

    const sortedFloors = [...floors].sort(
      // @ts-ignore
      (floorA, floorB) => floorA.number - floorB.number
    );

    const options: { value: string; label: string }[] = [];

    sortedFloors.forEach((floor) => {
      if (floor.code) {
        options.push({
          value: floor.code,
          label: `${t('FLOOR_NUMBER_LABEL_TEXT')} ${floor.number}`,
        });
      }
      floor.spaces.forEach((space) => {
        if (space.code) {
          options.push({
            value: space.code,
            label: `${t('FLOOR_NUMBER_LABEL_TEXT')} ${floor.number},${t(
              'SPACE_NAME_LABEL_TEXT'
            )} ${space.name}`,
          });
        }
      });
    });

    setPlacesOptions(options);
  }, [floors]);

  useEffect(() => {
    if (Number.isInteger(props.form.control._formValues.categoryId)) {
      setIsCategoryChosen(true);
    } else {
      setChosenCategory(undefined);
      setIsCategoryChosen(false);
    }
  }, [props.form.control._formValues.categoryId]);

  const {
    control,
    formState: { errors },
  } = props.form;

  const categoriesOpts = supportCategories.map((supportCategory) => {
    return {
      value: supportCategory.id,
      label: supportCategory.name,
      icon: supportCategory.icon,
    };
  });

  const categoriesOptions = profile.company?.companyUniqueVisitId!
    ? [
        ...categoriesOpts,
        {
          value: -1,
          label: t('SERVICE_CALL_UNIQUE_CATEGORY'),
          icon: profile.company?.name!,
        },
      ]
    : categoriesOpts;

  useEffect(() => {
    props.form.setValue('buildingVisitId', profile.company?.buildings[0].code);
    props.form.setValue(
      'spaceVisitId',
      profile.company?.buildings[0].floor?.code
    );
  }, [profile.company?.buildings[0].code]);

  const subCategoriesOptions: { value: string | number; label: string }[] =
    chosenCategory === -1
      ? [
          {
            value: '63876583430f6d353f6e3d6c',
            label: 'ניקיון',
          },
          {
            value: '6387658f430f6d14706e3d72',
            label: 'תחזוקת מקרר/מיקרוגל/מכונת קפה/תמי 4',
          },
          {
            value: '63876599430f6d16ee6e3d74',
            label: 'תחזוקת תאורה / קירות / נזילות / מיזוג',
          },
          {
            value: '638765a0430f6d44f66e3d79',
            label: 'מוצרי מטבח',
          },
        ]
      : supportSubCategories.map((supportSubCategory) => {
          return {
            value: supportSubCategory.id,
            label: supportSubCategory.name,
          };
        });

  const subSubCategoriesOptions = supportSubSubCategories.map((item) => {
    return {
      label: item.name,
      value: item.id,
    };
  });
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const handleCheckboxChange = (value: string) => {
    setSelectedOptions((prevSelected) =>
      prevSelected.includes(value)
        ? prevSelected.filter((item) => item !== value)
        : [...prevSelected, value]
    );
  };

  const onSubSubCategoryCheckboxClick = (item: string) => {
    const currentMessageValue = props.form.getValues('message');

    // Check if the item is already in the textarea
    const itemExists = currentMessageValue?.includes(`-${item}`);

    if (itemExists) {
      // If the item exists, remove it by replacing it with an empty string
      const updatedMessageValue = currentMessageValue
        .replace(`-${item}`, '')
        .replace(/\n{2,}/g, '\n');
      props.form.setValue('message', updatedMessageValue.trim()); // Trim to remove leading/trailing whitespace
    } else {
      // If the item doesn't exist, add it as before
      const updatedMessageValue = `${
        currentMessageValue ? `${currentMessageValue}\n` : ''
      }-${item}`;
      props.form.setValue('message', updatedMessageValue.trim()); // Trim to remove leading/trailing whitespace
    }
  };
  const serviceCallCategoryIcon = (icon: string) => {
    switch (icon) {
      case ServiceCallCategoryIconTypeEnum.REACT_FEATHER_SHIELD:
        return <Shield size={30} />;
      case ServiceCallCategoryIconTypeEnum.REACT_FEATHER_NOISE:
        return <Volume2 size={30} />;
      case ServiceCallCategoryIconTypeEnum.REACT_FEATHER_WINDOW:
        return <Columns size={30} />;
      case ServiceCallCategoryIconTypeEnum.REACT_FEATHER_ELECTRICITY:
        return <Zap size={30} />;
      case ServiceCallCategoryIconTypeEnum.REACT_FEATHER_WIND:
        return <Wind size={30} />;
      case ServiceCallCategoryIconTypeEnum.REACT_FEATHER_TOOL:
        return <Tool size={30} />;
      case ServiceCallCategoryIconTypeEnum.REACT_FEATHER_DROPLET:
        return <Droplet size={30} />;
      case ServiceCallCategoryIconTypeEnum.REACT_FEATHER_ALERT_OCTAGON:
        return <AlertOctagon size={30} />;
      case ServiceCallCategoryIconTypeEnum.REACT_FEATHER_SETTINGS:
        return <Settings size={30} />;
      case ServiceCallCategoryIconTypeEnum.REACT_FEATHER_CREDIT_CARDS:
        return <CreditCard size={30} />;
      case ServiceCallCategoryIconTypeEnum.REACT_FEATHER_HELP:
        return <HelpCircle size={30} />;
      case ServiceCallCategoryIconTypeEnum.REACT_FEATHER_MESSAGE_SQUARE:
        return <MessageSquare size={30} />;
      default:
        return <Settings size={30} />;
    }
  };

  const onCardChosen = (categoryId: number) => {
    setChosenCategory(categoryId);
  };

  useEffect(() => {
    if (props.form.watch('subCategoryId')) {
      // Clear the selected checkboxes when subCategoryId changes
      setSelectedOptions([]);
      // Clear the message field
      props.form.setValue('message', '');
    }
  }, [props.form.watch('subCategoryId')]);

  return (
    <>
      {!isCategoryChosen ? (
        <Col className="form-user-input">
          <Controller
            control={control}
            rules={{
              required: {
                value: true,
                message: t('FORM_ERRORS_FIELD_IS_REQUIRED'),
              },
            }}
            defaultValue={categoriesOptions.map((c) => c.value)}
            name="categoryId"
            render={({ field: { onChange } }) => (
              <Row style={{ margin: '0', justifyContent: 'center' }}>
                {categoriesOptions.map((category) => {
                  return (
                    <Col
                      key={category.value}
                      className="category-col"
                      md={6}
                      style={{
                        flex: categoriesOptions.length === 4 ? 'unset' : '1 1',
                      }}
                    >
                      <Card
                        className={classNames('category-card', {
                          chosen: chosenCategory === category.value,
                        })}
                        onClick={() => {
                          onCardChosen(category.value);
                          onChange(category.value);
                        }}
                        id={'categoryId'}
                      >
                        <div className="category-card-title">
                          {serviceCallCategoryIcon(category.icon)}
                        </div>
                        <CardBody className="category-card-body">
                          <div className="category-card-body-label">
                            {category.label}
                          </div>
                        </CardBody>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            )}
          />
          {errors.categoryId ? (
            <FormFeedback className="form-input-error">
              {errors.categoryId.message}
            </FormFeedback>
          ) : null}
        </Col>
      ) : (
        <>
          <Row>
            <Col md="6" className="form-user-input">
              <Label for="buildingVisitId">
                <span>* </span>
                {t('BUILDING')}
              </Label>
              <Controller
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: t('FORM_ERRORS_FIELD_IS_REQUIRED'),
                  },
                }}
                name="buildingVisitId"
                render={({ field: { onChange, value } }) => (
                  <Select
                    key={value}
                    id={'buildingVisitId'}
                    value={buildingsOption.filter((x) => x.value === value)}
                    onChange={(val) => onChange(val?.value)}
                    options={buildingsOption}
                    theme={selectThemeColors}
                    classNamePrefix="select"
                  />
                )}
              />
              {errors.buildingVisitId ? (
                <FormFeedback className="form-input-error">
                  {errors.buildingVisitId.message}
                </FormFeedback>
              ) : null}
            </Col>
            <Col md="6" className="form-user-input">
              <Label for="spaceVisitId">
                <span>* </span>
                {t('PLACE')}
              </Label>
              <Controller
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: t('FORM_ERRORS_FIELD_IS_REQUIRED'),
                  },
                }}
                name="spaceVisitId"
                render={({ field: { onChange, value } }) => (
                  <Select
                    id={'spaceVisitId'}
                    isDisabled={!props.form.watch('buildingVisitId')}
                    value={placesOptions.filter((x) => x.value === value)}
                    onChange={(val) => onChange(val?.value)}
                    options={placesOptions}
                    theme={selectThemeColors}
                    classNamePrefix="select"
                  />
                )}
              />
              {errors.spaceVisitId ? (
                <FormFeedback className="form-input-error">
                  {errors.spaceVisitId.message}
                </FormFeedback>
              ) : null}
            </Col>
          </Row>
          <Row>
            <Col md="6" className="form-user-input">
              <Label for="subCategoryId">
                <span>* </span>
                {t('CREATE_NEW_SERVICE_CALL_SUB_CATEGORY_FIELD')}
              </Label>
              <Controller
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: t('FORM_ERRORS_FIELD_IS_REQUIRED'),
                  },
                }}
                defaultValue={subCategoriesOptions.map((c) => c.value)}
                name="subCategoryId"
                render={({ field: { onChange, value } }) => (
                  <Select
                    id={'subCategoryId'}
                    isDisabled={!props.form.watch('categoryId')}
                    value={subCategoriesOptions.filter(
                      (c) => value === c.value
                    )}
                    onChange={(val) => {
                      onChange(val?.value);
                      props.form.setValue('message', '');
                    }}
                    options={subCategoriesOptions}
                    theme={selectThemeColors}
                    classNamePrefix="select"
                  />
                )}
              />
              {errors.subCategoryId ? (
                <FormFeedback className="form-input-error">
                  {errors.subCategoryId.message}
                </FormFeedback>
              ) : null}
            </Col>
            <Col md="6" className="form-user-input">
              <Label for="subCategoryId">
                {t('CREATE_NEW_SERVICE_CALL_SUB_SUB_CATEGORY_FIELD')}
              </Label>
              <Controller
                name="subSubCategory"
                control={control}
                render={({ field }) => (
                  <div>
                    {subSubCategoriesOptions.map((option) => (
                      <div key={option.value}>
                        <Input
                          type="checkbox"
                          id={option.value.toString()}
                          value={option.value}
                          checked={selectedOptions.includes(
                            option.value.toString()
                          )}
                          onChange={() =>
                            handleCheckboxChange(option.value.toString())
                          }
                          onClick={() =>
                            onSubSubCategoryCheckboxClick(option.label)
                          }
                        />
                        <label htmlFor={option.value.toString()}>
                          &nbsp;{option.label}
                        </label>
                      </div>
                    ))}
                  </div>
                )}
              />
            </Col>
            <Col md="6" className="form-user-input">
              <Label for="subject">
                {t('CREATE_NEW_SERVICE_CALL_SUBJECT_FIELD')}
              </Label>
              <Controller
                name="subject"
                control={control}
                render={({ field }) => (
                  <Input
                    id="subject"
                    type="text"
                    placeholder={t('CREATE_NEW_SERVICE_CALL_SUBJECT_FIELD')}
                    {...field}
                  />
                )}
              />
            </Col>

            <Col md="12" className="form-user-input">
              <span>* </span>
              <Label for="message">
                {t('CREATE_NEW_SERVICE_CALL_DESCRIPTION_FIELD')}
              </Label>
              <Controller
                name="message"
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: t('FORM_ERRORS_FIELD_IS_REQUIRED'),
                  },
                }}
                render={({ field }) => (
                  <Input
                    id="message"
                    type="textarea"
                    maxLength={270}
                    className="category-code-textarea"
                    invalid={errors.message && true}
                    {...field}
                  />
                )}
              />
              {errors.message ? (
                <FormFeedback className="form-input-error">
                  {errors.message.message}
                </FormFeedback>
              ) : null}
            </Col>
            <Col md="12" className="form-user-input">
              <Label for="file">
                {t('CREATE_NEW_SERVICE_CALL_UPLOAD_FIELD')}
              </Label>
              <Controller
                name="files"
                control={control}
                render={({ field }) => (
                  <UploadMultiImageInput
                    id="file"
                    className="text-input"
                    placeholder={t('CREATE_NEW_SERVICE_CALL_UPLOAD_FIELD')}
                    invalid={errors.pdfUrl && true}
                    uploaderHook={uploadServiceCallFile}
                    {...field}
                    onChange={(value) => {
                      if (!value) {
                        clearSupportImage();
                      }
                      field.onChange(value);
                    }}
                  />
                )}
              />
              {errors.file ? (
                <FormFeedback className="form-input-error">
                  {errors.file.message}
                </FormFeedback>
              ) : null}
            </Col>
          </Row>
        </>
      )}
    </>
  );
};
